import React, { useState } from 'react';
import { TeamMember } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { AcademicCapIcon } from './icons';
import Modal from './Modal';
import { convertGoogleDriveUrl } from '../utils/imageHelpers';

interface TeamFlipGridProps {
  teamMembers: TeamMember[];
}

const TeamFlipGrid: React.FC<TeamFlipGridProps> = ({ teamMembers }) => {
  const { colors } = useTheme();
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  
  const toggleFlip = (memberId: string) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {teamMembers.map((member, index) => (
          <div
            key={member._id}
            className={`relative h-[400px] md:h-[500px] animate-fadeInScale stagger-${index + 1}`}
            style={{ perspective: '1000px' }}
          >
            <div
              className={`
                absolute inset-0 w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer
                ${flippedCards.has(member._id) ? 'rotate-y-180' : ''}
              `}
              onClick={() => toggleFlip(member._id)}
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* Front Side */}
              <div
                className="absolute inset-0 w-full h-full rounded-2xl overflow-hidden backface-hidden"
                style={{
                  backfaceVisibility: 'hidden',
                  backgroundColor: colors.background.paper,
                  boxShadow: `0 10px 30px -10px ${colors.accent.mint}20`
                }}
              >
                {/* Image Section */}
                <div className="relative h-2/3 overflow-hidden">
                  <img
                    src={member.avatarImage || member.profileImage || (member.imageUrl ? convertGoogleDriveUrl(member.imageUrl) : `https://picsum.photos/seed/${member._id}/400/400`)}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                  <div 
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(to bottom, transparent 50%, ${colors.background.paper} 100%)`
                    }}
                  />
                  
                  {/* Floating Badge */}
                  <div 
                    className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold text-white animate-float"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`
                    }}
                  >
                    Expert
                  </div>
                </div>
                
                {/* Basic Info */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-bold mb-1" style={{ color: colors.text.primary }}>
                    {member.name}
                  </h3>
                  <p className="text-sm font-medium mb-3" style={{ color: colors.accent.mint }}>
                    {member.role}
                  </p>
                  
                  {/* Flip Indicator */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: colors.text.secondary }}>
                      Click to learn more
                    </span>
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center animate-pulse"
                      style={{ backgroundColor: `${colors.accent.mint}20` }}
                    >
                      <svg 
                        className="w-4 h-4" 
                        fill={colors.accent.mint} 
                        viewBox="0 0 20 20"
                      >
                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Back Side */}
              <div
                className="absolute inset-0 w-full h-full rounded-2xl overflow-hidden backface-hidden rotate-y-180 p-6"
                style={{
                  backfaceVisibility: 'hidden',
                  backgroundColor: colors.background.paper,
                  boxShadow: `0 10px 30px -10px ${colors.accent.mint}20`,
                  transform: 'rotateY(180deg)'
                }}
              >
                <div className="h-full flex flex-col">
                  {/* Compact Header */}
                  <div className="text-center mb-2">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <div className="flex-shrink-0">
                        {member.avatarImage ? (
                          <img 
                            src={member.avatarImage}
                            alt={member.name}
                            className="w-12 h-12 rounded-full object-cover"
                            style={{
                              border: `2px solid ${colors.accent.mint}`
                            }}
                          />
                        ) : (
                          <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center"
                            style={{
                              background: `linear-gradient(135deg, ${colors.primary.main}20 0%, ${colors.accent.mint}20 100%)`
                            }}
                          >
                            <AcademicCapIcon className="w-7 h-7" style={{ color: colors.accent.mint }} />
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg font-bold leading-tight" style={{ color: colors.text.primary }}>
                          {member.name}
                        </h3>
                        <p className="text-xs" style={{ color: colors.accent.mint }}>
                          {member.role}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Bio - Primary Focus */}
                  <div className="flex-grow mb-3 overflow-hidden" style={{ minHeight: '200px', maxHeight: '250px' }}>
                    <h4 className="text-lg font-semibold mb-2" style={{ color: colors.text.primary }}>About</h4>
                    <div className="h-full overflow-y-auto pr-2 team-bio-scrollbar">
                      <p className="text-base leading-relaxed" style={{ color: colors.text.secondary }}>
                        {member.bio || 'No bio available'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Compact Specialties */}
                  {member.specialties && member.specialties.length > 0 && (
                    <div className="mb-3">
                      <h4 className="text-base font-semibold mb-2" style={{ color: colors.text.primary }}>
                        Key Expertise:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {member.specialties.slice(0, 2).map((specialty, idx) => (
                          <span 
                            key={idx}
                            className="text-sm px-3 py-1.5 rounded-full"
                            style={{
                              backgroundColor: `${colors.accent.mint}15`,
                              color: colors.accent.mint
                            }}
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Compact Action */}
                  <div className="mt-auto">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedMember(member);
                      }}
                      className="w-full py-2.5 rounded-md font-medium text-base transition-all duration-300 hover:scale-[1.02]"
                      style={{
                        background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                        color: 'white'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
                      }}
                    >
                      View Full Profile
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal for Full Profile */}
      {selectedMember && (
        <Modal
          isOpen={!!selectedMember}
          onClose={() => setSelectedMember(null)}
          title={selectedMember.name}
        >
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <img
                src={selectedMember.profileImage || selectedMember.avatarImage || (selectedMember.imageUrl ? convertGoogleDriveUrl(selectedMember.imageUrl) : `https://picsum.photos/seed/${selectedMember._id}/200/200`)}
                alt={selectedMember.name}
                className="w-24 h-24 rounded-full object-cover"
                style={{ border: `3px solid ${colors.accent.mint}` }}
              />
              <div>
                <h3 className="text-xl font-bold" style={{ color: colors.text.primary }}>
                  {selectedMember.role}
                </h3>
                <p className="text-sm" style={{ color: colors.text.secondary }}>
                  Expert Instructor
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2" style={{ color: colors.text.primary }}>About</h4>
              <p style={{ color: colors.text.secondary }}>{selectedMember.bio}</p>
            </div>
            
            {selectedMember.specialties && selectedMember.specialties.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2" style={{ color: colors.text.primary }}>Specialties</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedMember.specialties.map((specialty, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm"
                      style={{
                        backgroundColor: `${colors.accent.mint}15`,
                        color: colors.accent.mint
                      }}
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default TeamFlipGrid;

// Add custom CSS for 3D transforms
const style = document.createElement('style');
style.textContent = `
  .backface-hidden {
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }
  
  .transform-style-preserve-3d {
    transform-style: preserve-3d;
  }
  
  .rotate-y-180 {
    transform: rotateY(180deg);
  }
`;
document.head.appendChild(style);